import { ReactNode } from "react";
import { Autocomplete, TextField, FormControl, Box } from "@mui/material";
import LabelInput from "./LabelInput";
import {
  Controller,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { MenuData } from "./CustomSelect";
import { t } from "i18next";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { CustomFormHelperText } from "./CustomFormHelperText";
import { common, input } from "../../i18n";

interface Props {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  label: string;
  info: string;
  placeholder: string;
  listMenuItem: MenuData[];
  required?: boolean;
  disabled?: boolean;
  defaultValue?: any;
  showLabelInput?: boolean;
  freeSolo?: boolean;
  /** custom row content in the open list; the input and the search still use the plain label */
  renderOptionLabel?: (option: MenuData) => ReactNode;
}

export const CustomAutoComplete = (props: Props) => {
  const { i18n } = useTranslation([common, input], { nsMode: "fallback" });
  // read here: renderOption below names its own first argument `props`
  const { renderOptionLabel } = props;

  return (
    <FormControl fullWidth>
      <Controller
        key={props.name}
        name={props.name}
        control={props.control}
        rules={props.rules}
        defaultValue={props.defaultValue}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          const isFreeSolo = props.freeSolo === true;

          const currentValue = isFreeSolo
            ? value ?? ""
            : props.listMenuItem.find((opt) => opt.value === value) || null;

          return (
            <Box>
              {props.showLabelInput !== false && (
                <LabelInput
                  label={props.label}
                  info={props.info}
                  required={props.required}
                />
              )}

              <Autocomplete
                id={props.name}
                fullWidth
                size="small"
                disabled={props.disabled}
                multiple={false}
                freeSolo={isFreeSolo}
                value={currentValue}
                onChange={(event, selectedOption) => {
                  if (isFreeSolo) {
                    if (typeof selectedOption === "string") {
                      onChange(selectedOption);
                    } else if (selectedOption?.inputValue) {
                      // กรณีใช้ create option
                      onChange(selectedOption.inputValue);
                    } else {
                      onChange(
                        selectedOption
                          ? selectedOption.value || selectedOption
                          : "",
                      );
                    }
                  } else {
                    onChange(selectedOption ? selectedOption.value : null);
                  }
                }}
                options={props.listMenuItem}
                isOptionEqualToValue={(option, val) =>
                  option.value === (val?.value ?? val)
                }
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  return i18n.language.includes("th")
                    ? option.nameTH ?? t("undefined")
                    : option.nameEN ?? t("undefined");
                }}
                getOptionDisabled={(option) => option.disabled ?? false}
                popupIcon={<KeyboardArrowDownIcon />}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.value === "add" && (
                      <img
                        src="/img/add.svg"
                        alt="add"
                        style={{ marginRight: 8 }}
                      />
                    )}
                    {renderOptionLabel
                      ? renderOptionLabel(option)
                      : i18n.language.includes("th")
                        ? option.nameTH
                        : option.nameEN}
                  </li>
                )}
                noOptionsText={t("noRowsLabel")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      props.showLabelInput === false ? props.label : undefined
                    }
                    placeholder={props.placeholder}
                    error={!!error}
                    sx={{ height: 40, backgroundColor: "var(--white)" }}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      if (isFreeSolo) onChange(e.currentTarget.value);
                    }}
                  />
                )}
              />
              <CustomFormHelperText error={error} />
            </Box>
          );
        }}
      />
    </FormControl>
  );
};
